import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth';
import { API_BASE_URL } from '../config';
import { Button } from '../components/Button';
import { Alert } from '../components/Alert';
import { Modal } from '../components/Modal';

type QRCode = {
  id: string;
  target: string;
  imageUrl?: string;
};

const DashboardPage: React.FC = () => {
  const { user, jwt, logout } = useAuth();
  const [qrs, setQrs] = useState<QRCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createType] = useState('url');
  const [createTarget, setCreateTarget] = useState('');
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  function fetchQRCodes() {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE_URL}`, {
      headers: {
        Authorization: jwt ? `Bearer ${jwt}` : '',
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch QR codes');
        return res.json();
      })
      .then((data) => setQrs(data.qrCodes || []))
      .catch((e) => setError(e.message || 'Unknown error'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchQRCodes();
    // eslint-disable-next-line
  }, [jwt]);

  async function handleDownload(id: string) {
    try {
      const url = `${API_BASE_URL.replace(/\/qr.*/, '')}/r/${id}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch QR code image');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `qr-${id}.png`;
      link.click();
      setSuccess('QR code downloaded.');
    } catch (e: any) {
      setError(e.message || 'Download failed');
    }
  }

  async function handleDelete(id: string) {
    setDeleteLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: jwt ? `Bearer ${jwt}` : '' },
      });
      if (!res.ok) throw new Error('Failed to delete QR code');
      setSuccess('QR code deleted.');
      fetchQRCodes();
    } catch (e: any) {
      setError(e.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
        <h1 className="text-2xl font-bold mb-2 sm:mb-0">My QR Codes</h1>
        <div className="flex items-center space-x-2">
          <span className="text-gray-700">
            {user?.name} ({user?.email})
          </span>
          <Button variant="secondary" onClick={logout}>
            Log out
          </Button>
        </div>
      </div>

      {error && (
        <Alert type="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <div className="mb-4 flex justify-end">
        <Button onClick={() => setShowCreate(true)} variant="primary">
          + Create QR Code
        </Button>
      </div>

      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create QR Code"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setCreating(true);
            setError(null);
            setSuccess(null);
            try {
              const res = await fetch(`${API_BASE_URL}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: jwt ? `Bearer ${jwt}` : '',
                },
                body: JSON.stringify({
                  type: createType,
                  target: createTarget,
                }),
              });
              if (!res.ok) throw new Error('Failed to create QR code');
              setShowCreate(false);
              setCreateTarget('');
              setSuccess('QR code created!');
              fetchQRCodes();
            } catch (e: any) {
              setError(e.message || 'Unknown error');
            } finally {
              setCreating(false);
            }
          }}
        >
          <div className="mb-2">
            <label className="block mb-1 font-medium" htmlFor="type">
              Type
            </label>
            <select
              id="type"
              className="border rounded px-2 py-1 w-full"
              value={createType}
              disabled
              aria-disabled="true"
            >
              <option value="url">URL</option>
            </select>
          </div>
          <div className="mb-2">
            <label className="block mb-1 font-medium" htmlFor="target">
              Target (URL)
            </label>
            <input
              id="target"
              className="border rounded px-2 py-1 w-full"
              value={createTarget}
              onChange={(e) => setCreateTarget(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="flex justify-end mt-4">
            <Button
              type="submit"
              variant="primary"
              loading={creating}
              disabled={creating || !createTarget}
            >
              Create
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete QR Code"
      >
        <div className="mb-4">
          Are you sure you want to delete this QR code?
        </div>
        <div className="flex justify-end space-x-2">
          <Button
            variant="secondary"
            onClick={() => setDeleteId(null)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteId && handleDelete(deleteId)}
            loading={deleteLoading}
            disabled={deleteLoading}
          >
            Delete
          </Button>
        </div>
      </Modal>

      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 rounded">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">QR Code</th>
                <th className="px-4 py-2 text-left">Target</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {qrs.map((qr) => (
                <tr key={qr.id} className="border-t">
                  <td className="px-4 py-2 font-mono">{qr.id}</td>
                  <td className="px-4 py-2 break-all">{qr.target}</td>
                  <td className="px-4 py-2 space-x-2">
                    <a
                      href={`/qr/${qr.id}`}
                      className="text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      Edit
                    </a>
                    <span>|</span>
                    <a
                      href={`/qr/${qr.id}/analytics`}
                      className="text-green-600 hover:underline focus:outline-none focus:ring-2 focus:ring-green-400"
                    >
                      Analytics
                    </a>
                    <span>|</span>
                    <Button
                      variant="secondary"
                      onClick={() => handleDownload(qr.id)}
                      className="!inline px-2 py-1"
                      style={{ minWidth: 0 }}
                    >
                      Download
                    </Button>
                    <span>|</span>
                    <Button
                      variant="danger"
                      onClick={() => setDeleteId(qr.id)}
                      className="!inline px-2 py-1"
                      style={{ minWidth: 0 }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
